import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Search, 
  Briefcase, 
  Users, 
  FileText, 
  Link2, 
  X,
  Loader2,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function UniversalSearch({ className = '' }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({
    jobs: [],
    candidates: [],
    reports: [],
    links: []
  });
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults({ jobs: [], candidates: [], reports: [], links: [] });
      return;
    }

    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const performSearch = async (searchQuery) => {
    setIsLoading(true);
    try {
      // Fetch all data and filter client-side (could be optimized with a dedicated search endpoint)
      const [jobsRes, candidatesRes, reportsRes, linksRes] = await Promise.all([
        fetch(`${API}/api/jobs`, { credentials: 'include' }),
        fetch(`${API}/api/candidates`, { credentials: 'include' }),
        fetch(`${API}/api/reports`, { credentials: 'include' }),
        fetch(`${API}/api/interview-links`, { credentials: 'include' })
      ]);

      const [jobs, candidates, reports, links] = await Promise.all([
        jobsRes.ok ? jobsRes.json() : [],
        candidatesRes.ok ? candidatesRes.json() : [],
        reportsRes.ok ? reportsRes.json() : [],
        linksRes.ok ? linksRes.json() : []
      ]);

      const q = searchQuery.toLowerCase();

      setResults({
        jobs: jobs.filter(j => 
          j.title?.toLowerCase().includes(q) || 
          j.department?.toLowerCase().includes(q) ||
          j.location?.toLowerCase().includes(q)
        ).slice(0, 5),
        candidates: candidates.filter(c => 
          c.name?.toLowerCase().includes(q) || 
          c.email?.toLowerCase().includes(q)
        ).slice(0, 5),
        reports: reports.filter(r => 
          r.candidate?.name?.toLowerCase().includes(q) ||
          r.job?.title?.toLowerCase().includes(q)
        ).slice(0, 5),
        links: links.filter(l => 
          l.link_id?.toLowerCase().includes(q)
        ).slice(0, 3)
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (type, item) => {
    setIsOpen(false);
    setQuery('');
    
    switch (type) {
      case 'job':
        navigate(`/jobs?highlight=${item.job_id}`);
        break;
      case 'candidate':
        navigate(`/candidates?highlight=${item.candidate_id}`);
        break;
      case 'report':
        navigate(`/reports?highlight=${item.session_id}`);
        break;
      case 'link':
        navigate(`/interview-links?highlight=${item.link_id}`);
        break;
    }
  };

  const hasResults = results.jobs.length > 0 || results.candidates.length > 0 || 
                     results.reports.length > 0 || results.links.length > 0;

  const getVerdictBadge = (verdict) => {
    const styles = {
      GREEN: 'bg-green-500/20 text-green-400 border-green-500/30',
      YELLOW: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      RED: 'bg-red-500/20 text-red-400 border-red-500/30',
      go: 'bg-green-500/20 text-green-400 border-green-500/30',
      no_go: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[verdict] || 'bg-zinc-500/20 text-zinc-400';
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div 
        className="relative cursor-pointer"
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder="Rechercher..."
          className="pl-10 pr-20 bg-zinc-100 border-zinc-200 focus:bg-white w-full md:w-80"
          data-testid="universal-search-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <kbd className="hidden md:inline-flex h-5 items-center gap-1 rounded border bg-zinc-200 px-1.5 text-[10px] font-medium text-zinc-500">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-zinc-200 overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          {isLoading && (
            <div className="p-4 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-400" />
            </div>
          )}

          {!isLoading && query && !hasResults && (
            <div className="p-8 text-center text-zinc-500">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Aucun résultat pour "{query}"</p>
            </div>
          )}

          {!isLoading && hasResults && (
            <div className="divide-y divide-zinc-100">
              {/* Jobs */}
              {results.jobs.length > 0 && (
                <div className="p-2">
                  <p className="px-3 py-1 text-xs font-medium text-zinc-500 uppercase">Postes</p>
                  {results.jobs.map(job => (
                    <button
                      key={job.job_id}
                      onClick={() => handleSelect('job', job)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 rounded-md text-left"
                    >
                      <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{job.title}</p>
                        <p className="text-xs text-zinc-500">{job.department} • {job.location}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-zinc-300" />
                    </button>
                  ))}
                </div>
              )}

              {/* Candidates */}
              {results.candidates.length > 0 && (
                <div className="p-2">
                  <p className="px-3 py-1 text-xs font-medium text-zinc-500 uppercase">Candidats</p>
                  {results.candidates.map(candidate => (
                    <button
                      key={candidate.candidate_id}
                      onClick={() => handleSelect('candidate', candidate)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 rounded-md text-left"
                    >
                      <div className="w-8 h-8 rounded-md bg-purple-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{candidate.name}</p>
                        <p className="text-xs text-zinc-500">{candidate.email}</p>
                      </div>
                      {candidate.interview_score && (
                        <Badge className={getVerdictBadge(candidate.interview_score >= 75 ? 'GREEN' : candidate.interview_score >= 50 ? 'YELLOW' : 'RED')}>
                          {Math.round(candidate.interview_score)}%
                        </Badge>
                      )}
                      <ArrowRight className="w-4 h-4 text-zinc-300" />
                    </button>
                  ))}
                </div>
              )}

              {/* Reports */}
              {results.reports.length > 0 && (
                <div className="p-2">
                  <p className="px-3 py-1 text-xs font-medium text-zinc-500 uppercase">Rapports</p>
                  {results.reports.map(report => (
                    <button
                      key={report.session_id}
                      onClick={() => handleSelect('report', report)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 rounded-md text-left"
                    >
                      <div className="w-8 h-8 rounded-md bg-amber-100 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{report.candidate?.name}</p>
                        <p className="text-xs text-zinc-500">{report.job?.title}</p>
                      </div>
                      {report.recommendation && (
                        <Badge className={getVerdictBadge(report.recommendation)}>
                          {report.recommendation === 'go' ? '🟢 GO' : '🔴 NO-GO'}
                        </Badge>
                      )}
                      <ArrowRight className="w-4 h-4 text-zinc-300" />
                    </button>
                  ))}
                </div>
              )}

              {/* Links */}
              {results.links.length > 0 && (
                <div className="p-2">
                  <p className="px-3 py-1 text-xs font-medium text-zinc-500 uppercase">Liens d'entretien</p>
                  {results.links.map(link => (
                    <button
                      key={link.link_id}
                      onClick={() => handleSelect('link', link)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 rounded-md text-left"
                    >
                      <div className="w-8 h-8 rounded-md bg-green-100 flex items-center justify-center">
                        <Link2 className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{link.link_id}</p>
                        <p className="text-xs text-zinc-500">Statut: {link.status}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-zinc-300" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick Actions when empty */}
          {!query && (
            <div className="p-4">
              <p className="text-xs font-medium text-zinc-500 mb-3">Actions rapides</p>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => { setIsOpen(false); navigate('/jobs'); }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-zinc-50 hover:bg-zinc-100 text-left"
                >
                  <Briefcase className="w-4 h-4 text-zinc-500" />
                  <span className="text-sm">Postes</span>
                </button>
                <button 
                  onClick={() => { setIsOpen(false); navigate('/candidates'); }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-zinc-50 hover:bg-zinc-100 text-left"
                >
                  <Users className="w-4 h-4 text-zinc-500" />
                  <span className="text-sm">Candidats</span>
                </button>
                <button 
                  onClick={() => { setIsOpen(false); navigate('/reports'); }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-zinc-50 hover:bg-zinc-100 text-left"
                >
                  <FileText className="w-4 h-4 text-zinc-500" />
                  <span className="text-sm">Rapports</span>
                </button>
                <button 
                  onClick={() => { setIsOpen(false); navigate('/interview-links'); }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-zinc-50 hover:bg-zinc-100 text-left"
                >
                  <Link2 className="w-4 h-4 text-zinc-500" />
                  <span className="text-sm">Liens</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
